<script lang="ts" setup>
import styled from 'vue-styled-components';
import { i18n } from '@/i18n';
import { PropType, computed, onMounted, reactive, ref } from 'vue';
import dayjs from 'dayjs';
import MonthCalendar from '@/components/calendar/calendarComponents/MonthCalendar.vue';

// interface
/**
 * Props用のinterface
 */
interface Props {
  showYearSelector?: boolean;
  organizationId?: string;
  changeColor?: string;
  activeDates?: Array<any>;
  value?: string | number;
  activeClass?: string;
  prefixClass?: string;
  hideWeekend?: boolean;
  hideSunday?: boolean;
}

/**
 * state用のinterface
 */
interface IYearCalendar {
  isUsingString: boolean | number;
  localLang: string;
}

// props
// propsのdefaultを設定
const props = withDefaults(defineProps<Props>(), {
  showYearSelector: true,
  organizationId: '',
  changeColor: 'red',
  activeDates: () => [],
  value: dayjs().year(),
  activeClass: '',
  prefixClass: 'calendar--active',
  hideWeekend: false,
  hideSunday: false,
});

// emits
const emits = defineEmits<{
  (e: 'input', val: any): void; // CalendarTable.vueのstate.selectYear
  (
    e: 'toggleDate',
    toggleDate: {
      date: string;
      selected: any;
      className: any;
      organizationId: string;
    }
  ): void; // 日付選択情報
  (e: 'update:activeDates', newDates: any[]); // 稼働日切り替えを行なった日付
  (e: 'monthClick', monthYearInfo: any);
}>();

// style variables
// const Wrapper = styled.section`
//   padding: 4em;
//   background: papayawhip;
// `;

//変数
// i18n対応
const t = i18n.t;
// state変数
const state: IYearCalendar = reactive<IYearCalendar>({ isUsingString: true, localLang: 'ja' }) as IYearCalendar;

//computed
/**
 * 言語設定の取得
 * ローカルの言語設定コードを取得する
 */
const lang = computed(() => {
  return i18n.locale;
});

/**
 * 月を構成する日付オブジェクトの作成・更新を行う(多分)
 */
const month = computed((): object => {
  const month = {};
  props.activeDates.forEach((date) => {
    let oDate;
    if (typeof date === 'string') {
      oDate = {
        date: date,
        className: props.activeClass,
      };
    } else {
      // activeDateのオブジェクトにclassName属性がない場合、自動的に空文字列が入ります。 そうでない場合は、未定義になります。
      oDate = {
        date: date.date,
        className: date.className || '',
      };
    }
    if (!month[dayjs(oDate.date).year()]) {
      month[dayjs(oDate.date).year()] = {};
    }
    // if (dayjs(oDate.date).year() !== parseInt(this.value)) return // 讓2020年1月的資料，不會放到 2019年的1月資料裡
    const m = (dayjs(oDate.date).month() + 1).toString();
    if (!month[dayjs(oDate.date).year()][m]) month[dayjs(oDate.date).year()][m] = [];
    month[dayjs(oDate.date).year()][m].push(oDate);
  });
  return month;
});

/**
 *
 */
const carryForwardFunction = computed(() => {
  let defaultFunction = (monthTitle) => String(monthTitle);
  switch (lang.value) {
    case 'ja':
      const year = String(activeYear.value + 1);
      defaultFunction = function (monthTitle) {
        return year + '年' + monthTitle;
      };
  }
  return defaultFunction;
});

/**
 * 稼働日カレンダーで選択される年度情報
 */
let activeYear = computed({
  get() {
    return props.value as number; // props.valueは、外側のv-model="year "から渡された値である。
  },
  set(val) {
    emits('input', val);
  },
});

// イベント
onMounted(() => {
  checkUsingString();
});

//関数
/**
 * props.activeDatesの存在確認とprops.activeDatesがstring[]かの確認
 * typeof props.activeDates[0] === 'string'がいるか怪しい
 * CalendarTable.vueの方で、activeDates: [] as ActiveDate[]で置かれているため、あり得ないんじゃないか?
 */
const checkUsingString = (): void => {
  state.isUsingString = props.activeDates.length && typeof props.activeDates[0] === 'string';
};

/**
 * 年度の切り替え
 * @param idx 年度のインデックス
 */
const changeYear = (idx: number): void => {
  activeYear.value = idx + activeYear.value - 3;
};

/**
 * 稼働日カレンダーの年度ラベルの表示部分
 * @param year 年
 */
const processYear = (year) => {
  let yearString = String(year);
  switch (lang.value) {
    case 'ja':
      yearString = yearString + '年度';
      break;
  }
  return yearString;
};

/**
 * 稼働日情報の更新
 * カレンダーの日付部分を選択した情報をもとに稼働日(登録年月日情報)の更新を行う
 * サーバーとのやりとりは無い、ここではあくまでオブジェクトの更新
 * なんでここにある?
 * @param dateObj 年月日の情報が入ったオブエクと
 */
const toggleDate = (dateObj) => {
  const activeDate = dayjs()
    .set('year', dateObj.year)
    .set('month', dateObj.month - 1)
    .set('date', dateObj.date)
    .format('YYYY-MM-DD');
  emits('toggleDate', {
    date: activeDate,
    selected: dateObj.selected,
    className: dateObj.className,
    organizationId: dateObj.organizationId,
  });

  let dateIndex;
  let newDates;

  if (state.isUsingString) {
    dateIndex = props.activeDates.indexOf(activeDate);
    newDates = modifiedActiveDates(dateIndex, activeDate);
  } else {
    const oDate = {
      date: activeDate,
      className: dateObj.className, // 本来はthis.defaultClassNameだが、バグを修正した
    };

    dateIndex = props.activeDates.indexOf(props.activeDates.find((i) => i.date === activeDate));
    newDates = modifiedActiveDates(dateIndex, oDate);
  }
  emits('update:activeDates', newDates);
};

/**
 * 稼働日の切り替え処理
 * カレンダーの日付部分をポチった時に状態を切り替えてるやつ
 * @param dateIndex 日付のインデックス
 * @param activeDate 稼働日情報
 */
const modifiedActiveDates = (dateIndex, activeDate) => {
  const newDates = [...props.activeDates];
  if (dateIndex === -1) {
    newDates.push(activeDate);
  } else {
    newDates.splice(dateIndex, 1);
  }
  return newDates;
};
const monthClick = (monthYearInfo) => {
  emits('monthClick', monthYearInfo);
};
</script>

<template>
  <div class="vue-calendar__container">
    <div v-if="showYearSelector" class="container__year">
      <!-- <span><button @click="addYear(-1)">back</button></span> -->
      <span v-for="i in 5" :key="i" class="year__chooser" @click="changeYear(i)">
        {{ processYear(i + activeYear - 3) }}
      </span>
      <!-- <span><button @click="addYear(1)">next</button></span> -->
    </div>
    <div class="container__months" :class="{ 'hide-weekend': hideWeekend, 'hide-sunday': hideSunday }">
      <template v-if="month[activeYear]">
        <MonthCalendar
          v-for="n in 9"
          :key="`month-${n + 3}-${activeYear}`"
          class="container__month"
          :year="activeYear"
          :month="n + 3"
          :active-dates="month[activeYear][n + 3]"
          :organization-id="organizationId"
          :active-class="activeClass"
          :prefix-class="prefixClass"
          :lang="lang"
          @toggleDate="toggleDate"
          @monthClickEvent="monthClick"
        />
      </template>
      <template v-else>
        <MonthCalendar
          v-for="n in 9"
          :key="`month-${n + 3}-${activeYear}`"
          class="container__month"
          :year="activeYear"
          :month="n + 3"
          :organization-id="organizationId"
          :active-class="activeClass"
          :prefix-class="prefixClass"
          :lang="lang"
          @toggleDate="toggleDate"
          @monthClickEvent="monthClick"
        />
      </template>
      <template v-if="month[activeYear + 1]">
        <MonthCalendar
          v-for="n in 3"
          :key="`month-${n}-${activeYear + 1}`"
          class="container__month"
          :year="activeYear + 1"
          :month="n"
          :active-dates="month[activeYear + 1][n]"
          :active-class="activeClass"
          :lang="lang"
          :organization-id="organizationId"
          :prefix-class="prefixClass"
          :process-month-title="carryForwardFunction"
          @toggleDate="toggleDate"
          @monthClickEvent="monthClick"
        />
      </template>
      <template v-else>
        <MonthCalendar
          v-for="n in 3"
          :key="`month-${n}-${activeYear + 1}`"
          class="container__month"
          :year="activeYear + 1"
          :month="n"
          :active-class="activeClass"
          :lang="lang"
          :organization-id="organizationId"
          :prefix-class="prefixClass"
          :process-month-title="carryForwardFunction"
          @toggleDate="toggleDate"
          @monthClickEvent="monthClick"
        />
      </template>
      <div class="container__month p-0" />
      <div class="container__month p-0" />
      <div class="container__month p-0" />
      <div class="container__month p-0" />
      <div class="container__month p-0" />
    </div>
  </div>
</template>

<style lang="css" scoped>
.vue-calendar__container {
  border-radius: 2px;
  min-width: 0;
  position: relative;
  text-decoration: none;
  box-shadow: 0 2px 1px -1px rgba(0, 0, 0, 0.2), 0 1px 1px 0 rgba(0, 0, 0, 0.14), 0 1px 3px 0 rgba(0, 0, 0, 0.12);
  background-color: #f6f6f3;
}
.vue-calendar__container .container__year {
  user-select: none;
  height: 65px;
  background-color: #fff;
  font-size: 24px;
  flex: 100%;
  text-align: center;
  display: flex;
}
.vue-calendar__container .container__year .year__chooser {
  height: 100%;
  flex: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.9);
}
.vue-calendar__container .container__year .year__chooser:hover {
  background-color: rgba(102, 102, 102, 0.1);
}
.vue-calendar__container .container__year .year__chooser:nth-child(4n-3) {
  color: rgba(0, 0, 0, 0.3);
}
.vue-calendar__container .container__year .year__chooser:nth-child(2n) {
  color: rgba(0, 0, 0, 0.6);
}
.vue-calendar__container .container__year .year__chooser:nth-child(3) {
  box-shadow: inset 0px -3px #4792bd;
}
.vue-calendar__container .container__months {
  flex-wrap: wrap;
  display: flex;
  padding: 15px;
}
.vue-calendar__container .container__month {
  padding: 8px;
  flex: 16.66%;
}
@media (max-width: 1300px) {
  .vue-calendar__container .container__month {
    flex: 25%;
  }
}
@media (max-width: 992px) {
  .vue-calendar__container .container__month {
    flex: 33.3%;
  }
}
@media (max-width: 768px) {
  .vue-calendar__container .container__month {
    flex: 50%;
  }
}
@media (max-width: 450px) {
  .vue-calendar__container .container__month {
    flex: 100%;
  }
}
.vue-calendar__container .p-0 {
  padding: 0px;
}
</style>
<style lang="css">
.container__months.hide-sunday .calendar__day:nth-of-type(7n) {
  display: none;
}
.container__months.hide-sunday .calendar__day {
  flex: 16.66%;
}
.container__months.hide-weekend .calendar__day:nth-of-type(7n),
.container__months.hide-weekend .calendar__day:nth-of-type(7n-1) {
  display: none;
}
.container__months.hide-weekend .calendar__day {
  flex: 19%;
}
</style>
